import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageBanner } from "@/components/ui/PageBanner";
import { IMG } from "@/lib/assets";
import { today, addDays, prettyDay } from "@/lib/perform";
import { SectionBlock, ScoreDialog, Whiteboard, HiddenWod, WodEditor } from "@/components/perform/WodParts";
import { ChevronLeft, ChevronRight, ClipboardList, CalendarDays, Timer, Plus, Users, CalendarCheck } from "lucide-react";
import { useBooking } from "@/components/perform/Timetable";

function DayStrip({ day, setDay }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(day, i - 3));
  return (
    <div className="flex items-center gap-2" data-testid="day-strip">
      <button onClick={() => setDay(addDays(day, -7))} className="p-2 rounded-full border border-[#343A4A] text-[#8A91A6] hover:text-white" aria-label="Previous week"><ChevronLeft className="w-4 h-4" /></button>
      <div className="grid grid-cols-7 gap-1.5 flex-1">
        {days.map((d) => {
          const on = d === day; const isToday = d === today();
          return (
            <button key={d} onClick={() => setDay(d)}
              className={`rounded-xl py-2 text-center border transition-colors ${on ? "bg-acid text-black border-acid" : "border-[#1E222A] bg-[#0F1115] text-[#B7BECB] hover:border-acid/50"}`}>
              <p className="mono-label" style={{ color: on ? "#000" : undefined }}>{prettyDay(d, { weekday: "short" })}</p>
              <p className="font-heading font-black text-lg leading-none mt-1">{Number(d.slice(8))}</p>
              {isToday && <span className={`block mx-auto mt-1 w-1.5 h-1.5 rounded-full ${on ? "bg-black" : "bg-acid"}`} />}
            </button>
          );
        })}
      </div>
      <button onClick={() => setDay(addDays(day, 7))} className="p-2 rounded-full border border-[#343A4A] text-[#8A91A6] hover:text-white" aria-label="Next week"><ChevronRight className="w-4 h-4" /></button>
    </div>
  );
}

// The club classes on this day: book one, or see the one you're booked on (your score here goes to your log)
function DayClasses({ day }) {
  const navigate = useNavigate();
  const { busy, book } = useBooking();
  const { data } = useQuery({ queryKey: ["calendar", day, day], queryFn: async () => (await api.get("/api/calendar", { params: { start: day, end: day } })).data });
  const cls = (data?.events || []).filter((e) => e.type === "class");
  if (!cls.length) return null;
  const mine = cls.find((e) => e.booked);
  const t = (e) => new Date(e.at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return (
    <div className="surface rounded-2xl p-4" data-testid="day-classes">
      {mine ? (
        <p className="text-sm flex items-center gap-2"><CalendarCheck className="w-4 h-4 text-[#B57BFF] shrink-0" />
          <span>You're booked on <b>{mine.title}</b> at {t(mine)}.{" "}
            {mine.done ? "Your workout is logged against it." : day <= today() ? "Log your score below and it goes straight to your training log." : "The workout appears here when your coach reveals it."}</span></p>
      ) : <p className="mono-label text-[#B57BFF] mb-2">classes on this day</p>}
      {!mine && (
        <div className="flex flex-wrap gap-2">
          {cls.map((e) => (
            <button key={e.id} onClick={() => book({ ...e, starts_at: e.at })} disabled={busy === e.id || e.booked_count >= e.capacity || new Date(e.at) < new Date()} data-testid="day-book"
              className="inline-flex items-center gap-2 h-9 px-4 rounded-full border border-[#343A4A] text-sm hover:border-acid disabled:opacity-40">
              <span className="font-mono2 text-acid">{t(e)}</span> {e.title} <span className="text-xs text-[#8A91A6]">{e.booked_count >= e.capacity ? "full" : "book"}</span>
            </button>
          ))}
          <button onClick={() => navigate("/app/timetable")} className="text-sm text-acid px-2">Timetable</button>
        </div>
      )}
    </div>
  );
}

function WodView({ w, onChanged }) {
  const [tab, setTab] = useState("workout");
  const [logIdx, setLogIdx] = useState(null);
  const { data: detail, refetch } = useQuery({ queryKey: ["wod", w.id], queryFn: async () => (await api.get(`/api/wods/${w.id}`)).data, enabled: !w.hidden });
  if (w.hidden) return <HiddenWod w={w} />;
  const mine = (i) => (w.my_scores || []).find((s) => s.section === i);
  const boards = detail?.whiteboards || [];
  const refresh = () => { refetch(); onChanged?.(); };
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="surface rounded-2xl overflow-hidden" data-testid="wod-card">
      <div className="relative p-5 border-b border-[#1E222A] flex flex-wrap items-end justify-between gap-3 overflow-hidden">
        <img src={w.sections.some((s) => s.kind === "strength") && !w.sections.some((s) => s.kind === "metcon") ? IMG.strength : IMG.conditioning} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg,#0F1115 30%,rgba(15,17,21,0.6) 75%,rgba(15,17,21,0.3))" }} />
        <div className="relative">
          <p className="mono-label text-acid flex items-center gap-1.5">{w.scope === "club" ? <><Users className="w-3.5 h-3.5" /> {w.club_name || "your club"}</> : "your own workout"}{w.time ? ` · ${w.time}` : ""}</p>
          <h2 className="font-heading font-black text-2xl tracking-tight mt-1">{w.title}</h2>
          {w.status === "draft" && <span className="mono-label text-[#FFB340]">draft: athletes can't see this yet</span>}
        </div>
        <div className="relative inline-flex rounded-full border border-[#343A4A] bg-[#0B0D11]/80 p-1">
          {["workout", "whiteboard"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
              className={`px-4 h-8 rounded-full text-sm capitalize ${tab === t ? "bg-acid text-black" : "text-[#8A91A6]"}`}>
              {t}{t === "whiteboard" && w.score_count ? ` (${w.score_count})` : ""}
            </button>
          ))}
        </div>
      </div>
      <div className="p-5 space-y-3">
        {tab === "workout" ? (
          w.sections.map((s, i) => <SectionBlock key={i} s={s} index={i} myScore={mine(i)} canLog onLog={setLogIdx} />)
        ) : boards.length === 0 ? (
          <p className="text-sm text-[#8A91A6]">Nothing scored in this workout.</p>
        ) : (
          boards.map((b) => <Whiteboard key={b.section} board={b} division={detail?.my_division} onChanged={refetch} />)
        )}
      </div>
      <ScoreDialog open={logIdx !== null} onOpenChange={(o) => !o && setLogIdx(null)} wod={w} section={logIdx ?? 0}
        existing={logIdx !== null ? mine(logIdx) : null} onSaved={() => { refresh(); setTab("whiteboard"); }} />
    </motion.div>
  );
}

export default function Today() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [day, setDay] = useState(/^\d{4}-\d{2}-\d{2}$/.test(params.get("day") || "") ? params.get("day") : today());
  const [edit, setEdit] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["wods", day], queryFn: async () => (await api.get("/api/wods", { params: { day } })).data });
  const wods = data?.wods || [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["wods", day] });
  const editorInit = useMemo(() => ({ date: day }), [day]);
  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.conditioning} overline={day === today() ? "today" : prettyDay(day, { weekday: "long" })} title="Workout of the Day"
        subtitle={data?.in_club ? "Your club's programming, your score, and the whiteboard. Log it and high-five your squad." : "Plan your session, log your score and watch your numbers move."}
        actions={
          <div className="flex flex-wrap gap-2">
            {data?.is_staff && (
              <button onClick={() => navigate("/app/programming")} data-testid="go-programming"
                className="inline-flex items-center gap-2 rounded-full h-10 px-5 bg-acid text-black font-medium"><ClipboardList className="w-4 h-4" /> Programming</button>
            )}
            <button onClick={() => navigate("/app/timer")} className="inline-flex items-center gap-2 rounded-full h-10 px-4 border border-white/30 backdrop-blur"><Timer className="w-4 h-4" /> Timer</button>
            <button onClick={() => navigate("/app/calendar")} className="inline-flex items-center gap-2 rounded-full h-10 px-4 border border-white/30 backdrop-blur"><CalendarDays className="w-4 h-4" /> Calendar</button>
          </div>} />
      <DayStrip day={day} setDay={setDay} />
      {user?.club_id && <DayClasses day={day} />}
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-bold text-lg">{prettyDay(day)}</h3>
        <button onClick={() => setEdit(true)} data-testid="add-own-wod" className="inline-flex items-center gap-1.5 text-sm text-acid hover:underline"><Plus className="w-4 h-4" /> Add my own workout</button>
      </div>
      {isLoading ? <p className="mono-label animate-pulse">loading…</p> : wods.length === 0 ? (
        <div className="surface rounded-2xl p-8 text-center" data-testid="no-wod">
          <p className="font-heading font-black text-xl">Nothing programmed for this day</p>
          <p className="text-sm text-[#8A91A6] mt-1 max-w-md mx-auto">
            {data?.is_staff ? "Program the day for your club, or let OCTON draft the week for you to approve."
              : data?.in_club ? "Your coach hasn't posted this day yet. You can still plan and log your own session."
              : "Plan a session, log a workout you've done, or ask your OCTON Coach to build your week."}
          </p>
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {data?.is_staff ? (
              <button onClick={() => navigate("/app/programming")} className="rounded-full h-10 px-5 bg-acid text-black font-medium">Open programming</button>
            ) : (
              <button onClick={() => setEdit(true)} className="rounded-full h-10 px-5 bg-acid text-black font-medium">Plan or log a workout</button>
            )}
            <button onClick={() => navigate("/app/coach")} className="rounded-full h-10 px-5 border border-[#343A4A]">Ask OCTON Coach</button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">{wods.map((w) => <WodView key={w.id} w={w} onChanged={refresh} />)}</div>
      )}
      <WodEditor open={edit} onOpenChange={setEdit} initial={editorInit} scope="personal" onSaved={refresh} />
      {!user?.club_id && user?.role === "client" && (
        <p className="text-xs text-[#565C6F] text-center">Training at a club? Enter your club code under Memberships to see its daily workouts and whiteboard.</p>
      )}
    </div>
  );
}
